export const CHECKLIST_TYPE_SELECTOR_STATUS = {
  UNLINKED: "unlinked",
  HSE_APPROVED: "hse_approved",
  HSE_PENDING: "hse_pending",
  HSE_DRAFT: "hse_draft",
};

export function getChecklistTypeHseBadge(type) {
  const hasHse = Boolean(type?.hse_document?.id || type?.source === "hse");
  if (!hasHse) {
    return { kind: "unlinked" };
  }

  const revStatus = type?.hse_revision_status || {};
  const approvedVersion = revStatus.approved_version_number;

  if (approvedVersion != null) {
    return {
      kind: "approved",
      version: approvedVersion,
      pendingRevision: revStatus.pending_revision === true,
    };
  }

  if (
    revStatus.draft_revision_in_progress ||
    revStatus.document_status === "pending_approval"
  ) {
    return {
      kind: "pending",
      version: revStatus.draft_version_number ?? null,
    };
  }

  return {
    kind: "draft",
    version: revStatus.draft_version_number ?? null,
  };
}

export function getChecklistTypeSelectorItems(type) {
  const published = type?.published_items;
  if (Array.isArray(published) && published.length > 0) {
    return published;
  }
  return type?.items || [];
}

export function classifyChecklistTypeForSelector(type) {
  const badge = getChecklistTypeHseBadge(type);

  switch (badge.kind) {
    case "approved":
      return CHECKLIST_TYPE_SELECTOR_STATUS.HSE_APPROVED;
    case "pending":
      return CHECKLIST_TYPE_SELECTOR_STATUS.HSE_PENDING;
    case "draft":
      return CHECKLIST_TYPE_SELECTOR_STATUS.HSE_DRAFT;
    default:
      return CHECKLIST_TYPE_SELECTOR_STATUS.UNLINKED;
  }
}

export function sortChecklistTypesForSelector(types = []) {
  const order = {
    [CHECKLIST_TYPE_SELECTOR_STATUS.HSE_APPROVED]: 0,
    [CHECKLIST_TYPE_SELECTOR_STATUS.HSE_PENDING]: 1,
    [CHECKLIST_TYPE_SELECTOR_STATUS.HSE_DRAFT]: 2,
    [CHECKLIST_TYPE_SELECTOR_STATUS.UNLINKED]: 3,
  };

  return [...types].sort((a, b) => {
    const statusA = order[classifyChecklistTypeForSelector(a)] ?? 9;
    const statusB = order[classifyChecklistTypeForSelector(b)] ?? 9;
    if (statusA !== statusB) return statusA - statusB;
    return (a.title || "").localeCompare(b.title || "", "nb");
  });
}

export function filterChecklistTypesByQuery(types = [], query = "") {
  const q = query.trim().toLowerCase();
  if (!q) return types;

  return types.filter((type) => {
    const title = (type.title || "").toLowerCase();
    const description = (type.description || "").toLowerCase();
    return title.includes(q) || description.includes(q);
  });
}

/** Default page size for checklist-type pickers (API default is ~25). */
export const CHECKLIST_TYPE_SELECTOR_PER_PAGE = 100;

/**
 * Params for GET /quality/checklists/types in type selectors.
 * Uses server-side `query` (indexed search) so older types outside the first page are found.
 */
export function buildChecklistTypeSelectorFetchParams({
  query = "",
  fields,
  page = 1,
  perPage = CHECKLIST_TYPE_SELECTOR_PER_PAGE,
  sortBy = "title",
  sort = "ASC",
} = {}) {
  const trimmed = String(query || "").trim();
  return {
    ...(fields ? { fields } : {}),
    page,
    per_page: perPage,
    sortBy,
    sort,
    ...(trimmed ? { query: trimmed } : {}),
  };
}

export function resolveChecklistCopySource({
  copySourceChecklist,
  mainReportChecklist,
  copySourceLabel,
  mainReport,
}) {
  const checklist = copySourceChecklist ?? mainReportChecklist ?? null;
  const items = checklist?.items;
  const hasItems = Array.isArray(items) && items.length > 0;
  const label =
    copySourceLabel ??
    mainReport?.report_number ??
    mainReport?.displayTitle ??
    mainReport?.title ??
    null;

  return {
    checklist: hasItems ? checklist : null,
    label,
    hasItems,
  };
}
